import React, { useCallback, useState } from 'react';
import { Upload, X, CheckCircle, Music, Image } from 'lucide-react';
import { Card } from '../common/Card';

interface FileUploadProps {
  label: string;
  accept: string;
  value?: File;
  onChange: (file: File | undefined) => void;
  error?: string;
  type: 'audio' | 'image';
  requirements?: string[];
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept,
  value,
  onChange,
  error,
  type,
  requirements = []
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [preview, setPreview] = useState<string>('');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    onChange(file);
    
    if (type === 'image' && file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target?.result as string || '');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeFile = () => {
    onChange(undefined);
    setPreview('');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
        </label>
        
        {value ? (
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {type === 'image' && preview ? (
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gold-500/10 rounded-lg flex items-center justify-center">
                    {type === 'audio' ? (
                      <Music className="w-6 h-6 text-gold-400" />
                    ) : (
                      <Image className="w-6 h-6 text-gold-400" />
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-white">{value.name}</p>
                  <p className="text-xs text-gray-400">{formatFileSize(value.size)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <button
                  onClick={removeFile}
                  className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </Card>
        ) : (
          <div
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center
              transition-all duration-200 cursor-pointer
              ${isDragOver 
                ? 'border-gold-400 bg-gold-500/5' 
                : error 
                  ? 'border-red-500 bg-red-500/5'
                  : 'border-gray-600 hover:border-gold-500 hover:bg-gold-500/5'
              }
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById(`file-input-${type}`)?.click()}
          >
            <div className="space-y-4">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                isDragOver ? 'bg-gold-500/20' : 'bg-gray-700'
              }`}>
                <Upload className={`w-8 h-8 ${isDragOver ? 'text-gold-400' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="text-lg font-medium text-white mb-2">
                  Drop your {type === 'audio' ? 'audio file' : 'cover art'} here
                </p>
                <p className="text-sm text-gray-400">
                  or <span className="text-gold-400">click to browse</span>
                </p>
              </div>
            </div>
            
            <input
              id={`file-input-${type}`}
              type="file"
              accept={accept}
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        )}
        
        {error && (
          <p className="text-sm text-red-400 mt-2">{error}</p>
        )}
      </div>
      
      {requirements.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-400 mb-2">Requirements:</h4>
          <ul className="space-y-1">
            {requirements.map((req, index) => (
              <li key={index} className="text-sm text-gray-300 flex items-center">
                <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2" />
                {req}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};